<template>
  <div class="health-panel">
    <section class="section card">
      <div class="section-head">
        <h3>总体情况</h3>
        <small>{{ data.asOfLabel || '' }}</small>
      </div>

      <div class="overview-top">
        <div class="total-revenue">
          <span class="label">总营收</span>
          <strong>{{ summary.totalRevenueLabel || '0' }}</strong>
        </div>
        <div ref="pieChart" class="pie-chart"></div>
      </div>

      <div class="metrics">
        <div class="metric-item red">
          <div class="metric-circle"><strong>{{ rentalVolume }}</strong></div>
          <span>房租出租量</span>
        </div>
        <div class="metric-item purple">
          <div class="metric-circle"><strong>{{ rentalRate }}<small>%</small></strong></div>
          <span>房屋出租率</span>
        </div>
        <div class="metric-item blue">
          <div class="metric-circle"><strong>{{ counselingOrders }}<small>单</small></strong></div>
          <span>心理咨询</span>
        </div>
        <div class="metric-item green">
          <div class="metric-circle"><strong>{{ therapyOrders }}<small>单</small></strong></div>
          <span>中医理疗</span>
        </div>
      </div>
    </section>

    <section class="section card">
      <h3>具体情况</h3>
      <div class="table-wrap">
        <table class="detail-table">
          <thead>
            <tr><th>项</th><th>金额</th><th>人员</th></tr>
          </thead>
          <tbody>
            <tr v-for="row in breakdown" :key="row.code">
              <td>{{ row.label }}</td>
              <td>{{ row.amountLabel }}</td>
              <td>{{ row.employeeName || '' }}</td>
            </tr>
            <tr v-if="!breakdown.length">
              <td colspan="3" class="empty-cell">暂无数据，请在 okradmin 导入健康事业部销售 Excel</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="section card">
      <h3>动态分析 <small>{{ data.monthLabel || '' }}</small></h3>
      <div ref="trendChart" class="trend-chart"></div>
    </section>
  </div>
</template>

<script>
const PIE_COLORS = ['#e32b32', '#2697e8', '#18a957', '#17a2b8', '#a855f7', '#b45309'];
const LABELS = {
  rent: '房租',
  deposit: '押金',
  downPayment: '定金',
  bottledWater: '桶装水',
  psychologicalCounseling: '心理咨询',
  tcmTherapy: '中医理疗'
};

export default {
  name: 'HealthSalesPanel',
  props: {
    data: { type: Object, default: () => ({}) }
  },
  computed: {
    summary() {
      return this.data.summary || {};
    },
    breakdown() {
      return this.data.revenueBreakdown || [];
    },
    rentalVolume() {
      return Math.round(Number(this.summary.rentalVolume || 0));
    },
    rentalRate() {
      return Math.round(Number(this.summary.rentalOccupancyRate || 0));
    },
    counselingOrders() {
      return Math.round(Number(this.summary.counselingOrders || 0));
    },
    therapyOrders() {
      return Math.round(Number(this.summary.therapyOrders || 0));
    }
  },
  watch: {
    data: {
      deep: true,
      handler() {
        this.$nextTick(() => this.renderCharts());
      }
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.$nextTick(() => this.renderCharts());
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    this.disposeCharts();
  },
  methods: {
    initChart(ref) {
      const el = this.$refs[ref];
      if (!el) return null;
      const chart = this.$echarts.init(el);
      el.__chart = chart;
      return chart;
    },
    disposeCharts() {
      ['pieChart', 'trendChart'].forEach(key => {
        const el = this.$refs[key];
        if (el && el.__chart) {
          el.__chart.dispose();
          el.__chart = null;
        }
      });
    },
    handleResize() {
      ['pieChart', 'trendChart'].forEach(key => {
        const el = this.$refs[key];
        if (el && el.__chart) el.__chart.resize();
      });
    },
    renderCharts() {
      this.renderPie();
      this.renderTrend();
    },
    renderPie() {
      const chart = this.initChart('pieChart');
      if (!chart) return;
      const data = this.breakdown
        .filter(r => Number(r.amount) > 0)
        .map(r => ({
          name: r.label,
          value: Number(r.amount),
          label: { formatter: `{b}\n{c}，{d}%` }
        }));
      if (!data.length) {
        chart.setOption({ title: { text: '暂无数据', left: 'center', top: 'center', textStyle: { color: '#bbb', fontSize: 12 } } });
        return;
      }
      chart.setOption({
        color: PIE_COLORS,
        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        series: [{
          type: 'pie',
          radius: ['42%', '68%'],
          center: ['50%', '50%'],
          label: { fontSize: 10, lineHeight: 14 },
          labelLine: { length: 6, length2: 8 },
          data
        }]
      });
    },
    renderTrend() {
      const chart = this.initChart('trendChart');
      if (!chart) return;
      const points = this.data.trend || [];
      if (!points.length) {
        chart.setOption({ title: { text: '暂无数据', left: 'center', top: 'center', textStyle: { color: '#bbb', fontSize: 12 } } });
        return;
      }
      const codes = Object.keys(LABELS);
      const dates = points.map(p => String(p.date).slice(5));
      const series = codes.map(code => ({
        name: LABELS[code],
        type: 'line',
        smooth: true,
        symbol: 'none',
        lineStyle: { width: 2 },
        data: points.map(p => Number(p[code] || 0))
      }));
      chart.setOption({
        color: PIE_COLORS,
        tooltip: { trigger: 'axis' },
        legend: {
          data: codes.map(c => LABELS[c]),
          bottom: 0,
          itemWidth: 12,
          itemHeight: 8,
          textStyle: { fontSize: 10, color: '#666' }
        },
        grid: { left: 40, right: 12, top: 20, bottom: 48 },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: dates,
          axisLabel: { fontSize: 9, color: '#999' }
        },
        yAxis: {
          type: 'value',
          axisLabel: { fontSize: 9, color: '#999', formatter: v => `${v}w` },
          splitLine: { lineStyle: { color: '#f0f0f0' } }
        },
        series
      });
    }
  }
};
</script>

<style lang="less" scoped>
.health-panel {
  padding-bottom: 24px;
}

.card {
  margin: 10px 10px 0;
  padding: 14px 12px 16px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(30, 35, 60, 0.06);
}

.section-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 10px;

  h3 {
    margin: 0;
    font-size: 16px;
    color: #303133;
  }

  small {
    color: #9aa1b2;
    font-size: 11px;
  }
}

.section > h3 {
  margin: 0 0 10px;
  font-size: 16px;
  color: #303133;

  small {
    color: #999;
    font-size: 12px;
    font-weight: normal;
  }
}

.overview-top {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.total-revenue {
  flex: 0 0 88px;
  text-align: center;

  .label {
    display: block;
    font-size: 12px;
    color: #666;
    margin-bottom: 4px;
  }

  strong {
    font-size: 22px;
    color: #d9272e;
  }
}

.pie-chart {
  flex: 1;
  height: 160px;
  min-width: 0;
}

.metrics {
  display: flex;
  justify-content: space-around;
  padding-top: 4px;

  .metric-item {
    display: flex;
    flex-direction: column;
    align-items: center;

    .metric-circle {
      width: 68px;
      height: 68px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;

      strong {
        font-size: 20px;
        font-weight: 700;
      }

      small {
        font-size: 11px;
        margin-left: 1px;
      }
    }

    span {
      margin-top: 8px;
      font-size: 11px;
      color: #666;
      text-align: center;
    }

    &.red .metric-circle { background: linear-gradient(135deg, #ff7e7e, #ff4d4f); }
    &.purple .metric-circle { background: linear-gradient(135deg, #c084fc, #a855f7); }
    &.blue .metric-circle { background: linear-gradient(135deg, #7aa8ff, #5b7cff); }
    &.green .metric-circle { background: linear-gradient(135deg, #4ade80, #16a34a); }
  }
}

.detail-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th, td {
    border: 1px solid #e8ebf0;
    padding: 10px 8px;
    text-align: center;
  }

  th {
    background: #4a4f63;
    color: #fff;
  }

  .empty-cell {
    color: #9aa1b2;
  }
}

.trend-chart {
  width: 100%;
  height: 240px;
}
</style>



// WEBPACK FOOTER //
// src/views/boss/HealthSalesPanel.vue